import { z } from 'zod';

/**
 * Group conversations: the user and several of their assistants (personas), each answering from
 * its own conversation. These are the room's shapes: its members, what was said, its state, and
 * what the page sends it.
 */

const id = z
  .string()
  .regex(/^[a-zA-Z0-9._-]{1,128}$/)
  .refine((value) => !['__proto__', 'constructor', 'prototype'].includes(value), 'Reserved ID.');

/** Members a group takes at most. */
export const MAX_GROUP_MEMBERS = 4;
/** What a member answers when it has nothing to add. */
export const GROUP_PASS_MARKER = '[[CROWDEX_GROUP_PASS]]';

/**
 * A member: one of the user's assistants (`persona`: its id; in a room made before assistants, its
 * avatar, an id of the call plugin's personas).
 */
export const groupMemberSchema = z
  .object({ id, name: z.string().trim().min(1).max(80), persona: id })
  .strict();

export const groupMessageSchema = z.object({
  id,
  sequence: z.number().int().nonnegative(),
  /** `user`, or a member's id. */
  authorId: z.string(),
  authorName: z.string(),
  /** The members it was for (named, picked), who answer it. */
  notifiedParticipantIds: z.array(id).max(MAX_GROUP_MEMBERS).optional(),
  text: z.string().max(100_000),
  recipientIds: z.array(id).max(MAX_GROUP_MEMBERS),
  replyTo: id.optional(),
  createdAt: z.string(),
});

export const groupSnapshotSchema = z.object({
  version: z.literal(1),
  conversationId: id,
  participants: z.array(groupMemberSchema).max(MAX_GROUP_MEMBERS),
  messages: z.array(groupMessageSchema),
  /** What each member heard: the last sequence it was told. */
  cursors: z.record(z.string(), z.number()),
  paused: z.boolean(),
  pauseReason: z.string().nullable(),
  /** Turns the members may still take before the user speaks again. */
  remainingTurns: z.number().int().nonnegative(),
  turnBudget: z.number().int().min(1).max(100),
  /** The members answering now. */
  activeParticipantIds: z.array(id),
  /** Why a member could not answer, by member. */
  errors: z.record(z.string(), z.string()),
  revision: z.number().int().nonnegative(),
});

/** A member's answer: words for the group (to members it names), or nothing to add. */
export const groupReplySchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('pass') }),
  z.object({
    action: z.literal('reply'),
    text: z.string().trim().min(1).max(100_000),
    recipientIds: z.array(id).max(MAX_GROUP_MEMBERS).default([]),
    replyTo: id.optional(),
  }),
]);

export const groupCommandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('status'), afterRevision: z.number().int().nonnegative().optional() }),
  z.object({ type: z.literal('pause') }),
  z.object({ type: z.literal('resume') }),
  z.object({
    type: z.literal('send'),
    id,
    text: z.string().trim().min(1).max(100_000),
    recipientIds: z.array(id).max(MAX_GROUP_MEMBERS).default([]),
    replyTo: id.optional(),
  }),
  z.object({
    type: z.literal('configure'),
    participants: z.array(groupMemberSchema).min(2).max(MAX_GROUP_MEMBERS),
    turnBudget: z.number().int().min(1).max(100).default(8),
  }),
]);

export type GroupMember = z.infer<typeof groupMemberSchema>;
export type GroupMessage = z.infer<typeof groupMessageSchema>;
export type GroupSnapshot = z.infer<typeof groupSnapshotSchema>;
export type GroupReply = z.infer<typeof groupReplySchema>;
export type GroupCommand = z.input<typeof groupCommandSchema>;
